'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { Search, X, Filter } from 'lucide-react';
import Link from 'next/link';
import {
  categoryColorClasses,
  categoryHoverClasses,
  categoryTextHoverClasses,
  categoryLinkClasses,
} from '@/data/blog-posts';
import type { BlogPostMetadata } from '@/lib/blog/types';
import { Calendar, ArrowRight } from 'lucide-react';

interface BlogSearchProps {
  posts: BlogPostMetadata[];
}

/**
 * Client-side blog search component with debounced input and category filtering
 *
 * Provides real-time search and filtering of blog posts by title, excerpt,
 * category, and keywords. Includes category dropdown filter and clear filters functionality.
 * Search input is debounced by 300ms to improve performance with large post lists.
 *
 * @param posts - Array of blog post metadata to search through
 *
 * @example
 * ```tsx
 * <BlogSearch posts={allPosts} />
 * ```
 */
export function BlogSearch({ posts }: BlogSearchProps) {
  const [searchInput, setSearchInput] = useState(''); // User input (not debounced)
  const [searchQuery, setSearchQuery] = useState(''); // Debounced search query
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Debounce search input to avoid excessive filtering on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Get unique categories from posts
  const categories = useMemo(() => {
    const cats = new Set(posts.map((post) => post.category));
    return Array.from(cats).sort();
  }, [posts]);

  // Filter posts based on search query and category
  const filteredPosts = useMemo(() => {
    let filtered = posts;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((post) => post.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((post) => {
        const titleMatch = post.title.toLowerCase().includes(query);
        const excerptMatch = post.excerpt.toLowerCase().includes(query);
        const categoryMatch = post.category.toLowerCase().includes(query);
        const keywordsMatch = post.keywords?.some((keyword) =>
          keyword.toLowerCase().includes(query)
        );
        return titleMatch || excerptMatch || categoryMatch || keywordsMatch;
      });
    }

    return filtered;
  }, [posts, searchQuery, selectedCategory]);

  const hasResults = filteredPosts.length > 0;
  const hasActiveFilters = searchQuery.trim() !== '' || selectedCategory !== 'all';

  return (
    <div className="mb-16">
      {/* Search and Filter Controls */}
      <div className="mb-8 space-y-4">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground z-10" />
          <input
            type="text"
            placeholder="Search articles by title, excerpt, category, or keywords..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-14 pr-14 py-5 bg-gradient-to-br from-card/95 via-card/90 to-card/85 backdrop-blur-md border-2 border-border/80 rounded-2xl text-foreground placeholder:text-muted-foreground/80 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 focus:shadow-lg focus:shadow-blue-500/10 transition-all duration-300 text-base font-medium shadow-lg"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              className="absolute right-5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1 hover:bg-card/50 rounded-full"
              aria-label="Clear search"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Category Filter */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Filter className="h-4 w-4" />
            <span>Category:</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                selectedCategory === 'all'
                  ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-300 border-2 border-blue-500/40 shadow-lg shadow-blue-500/10'
                  : 'bg-gradient-to-br from-card/60 via-card/40 to-card/20 backdrop-blur-sm text-muted-foreground hover:text-foreground border border-border/50 hover:border-blue-500/30 hover:shadow-md'
              }`}
            >
              All
            </button>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 text-blue-300 border-2 border-blue-500/40 shadow-lg shadow-blue-500/10'
                    : 'bg-gradient-to-br from-card/60 via-card/40 to-card/20 backdrop-blur-sm text-muted-foreground hover:text-foreground border border-border/50 hover:border-blue-500/30 hover:shadow-md'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Active Filters Summary */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>
              Showing {filteredPosts.length} of {posts.length} article
              {posts.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={() => {
                setSearchInput('');
                setSelectedCategory('all');
              }}
              className="text-blue-400 hover:text-blue-300 transition-colors underline"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* Results */}
      {!hasResults && hasActiveFilters ? (
        <div className="text-center py-12">
          <p className="text-lg text-muted-foreground mb-2">No articles found</p>
          <p className="text-sm text-muted-foreground">
            Try adjusting your search or category filter
          </p>
        </div>
      ) : (
        <div>
          <h2 className="text-3xl font-bold mb-8">
            {hasActiveFilters ? 'Search Results' : 'All Articles'}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {filteredPosts.map((post) => (
              <article
                key={post.href}
                className={`group relative bg-gradient-to-br from-card/80 via-card/60 to-card/40 backdrop-blur-md rounded-3xl p-8 border border-border/50 shadow-2xl hover:shadow-[0_20px_50px_rgba(0,0,0,0.3)] transition-all duration-500 hover:-translate-y-3 hover:scale-[1.02] flex flex-col overflow-hidden ${categoryHoverClasses[post.categoryColor]}`}
              >
                {/* Subtle gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 via-purple-500/0 to-pink-500/0 group-hover:from-blue-500/5 group-hover:via-purple-500/5 group-hover:to-pink-500/5 transition-all duration-500 rounded-3xl pointer-events-none" />

                <div className="relative z-10 flex flex-col flex-grow">
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <span
                      className={`px-3 py-1.5 ${categoryColorClasses[post.categoryColor]} text-xs font-bold rounded-full border backdrop-blur-sm shadow-sm`}
                    >
                      {post.category}
                    </span>
                    <span className="text-xs text-muted-foreground font-medium flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" />
                      {post.date}
                    </span>
                  </div>
                  <h3
                    className={`text-xl font-bold mb-4 text-foreground flex-grow transition-colors leading-tight ${categoryTextHoverClasses[post.categoryColor]}`}
                  >
                    <Link
                      href={post.href}
                      className="line-clamp-3 hover:opacity-90 transition-opacity"
                    >
                      {post.title}
                    </Link>
                  </h3>
                  <p className="text-sm text-muted-foreground mb-6 leading-relaxed line-clamp-3 flex-grow">
                    {post.excerpt}
                  </p>
                  <Link
                    href={post.href}
                    className={`${categoryLinkClasses[post.categoryColor]} font-semibold text-sm inline-flex items-center gap-2 mt-auto group/link`}
                  >
                    Read more
                    <ArrowRight className="h-4 w-4 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
